import { DESIGN_LIVE_REVIEW_METHOD } from '@99designs/design-services-common';
import { SelectProductEventInput } from '@99designs/graph-utils/types';
import { useBriefContext } from '../../../../BriefContext/BriefContext';
import { useResolveServiceProductMpvId } from '../../../hooks/useResolveServiceProductMpvId';
import { useUpdateField } from '../../useUpdateField';
import { useBriefSetProductDropdownMutation } from './products.generated';
import { useSelectProductWithCacheUpdate } from './useSelectProductMutation';

export function useUpdateProductDropdown(
    key: string,
    briefId?: string,
    initialValue?: string,
    groupId?: string
) {
    const { selectProduct } = useSelectProductWithCacheUpdate();
    const resolveServiceProductMpvId = useResolveServiceProductMpvId();
    const { serviceProductMpvId } = useBriefContext();

    const [setProductDropdown] = useBriefSetProductDropdownMutation({
        onCompleted: async (briefData) => {
            const fields = briefData.setField.dynamicFields.filter((field) => {
                const matchesKey = field.id === key;
                if (!groupId) return matchesKey;
                // Some schemas use groupId on ClientConfiguredField; guard access
                const fieldGroupId = (field as unknown as { groupId?: string }).groupId;
                return matchesKey && fieldGroupId === groupId;
            });
            if (fields.length === 0) {
                return;
            }

            for (const field of fields) {
                if (field.__typename !== 'ClientConfiguredField') {
                    throw new Error(
                        "Field is not a ClientConfiguredField. This schema is not compatible with the fields currently in use on the front end. And will cause all kinds of trouble. If you're seeing this error in bugsnag, investigate immediately"
                    );
                }
                const formValue = field.clientConfiguredValue;

                const input: SelectProductEventInput = {
                    printProductMpvId: [formValue],
                };

                input.serviceProductMpvId =
                    serviceProductMpvId.toLocaleLowerCase() ===
                    DESIGN_LIVE_REVIEW_METHOD.toLocaleLowerCase()
                        ? DESIGN_LIVE_REVIEW_METHOD
                        : await resolveServiceProductMpvId({
                              printProductMpvId: formValue,
                          });

                if (briefData.setField.correlationId && formValue) {
                    await selectProduct({
                        variables: {
                            requestId: briefData.setField.correlationId,
                            input: input,
                        },
                    });
                }
            }
        },
    });

    return useUpdateField({ key, setField: setProductDropdown, briefId, initialValue });
}

export default useUpdateProductDropdown;
